import { yupResolver } from "@hookform/resolvers/yup";
import FormDateV2 from "components/Common/Form/FormDateV2";
import FormInput from "components/Common/Form/FormInput";
import FormSelectModel from "components/Common/Form/FormSelectModel";
import {
  useCreatePrioritySchedule,
  useUpdatePrioritySchedule
} from "hooks";
import moment from "moment";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import * as yup from "yup";
import { getUserTypeOption, MODE_DETAIL, userTypeMode } from "./utils";

const validateSchema = (t, fixedUserType) => {
  const tLabel = key =>
    fixedUserType ? t(`${fixedUserType}.${key}`, { defaultValue: t(key) }) : t(key);
  const rules = {
    hub:
      yup
        .object()
        .required(t("message.required", { title: t("table.hub") }))
        .nullable(),
    route:
      yup
        .array()
        .min(1, t("message.required", { title: t("table.route") }))
        .nullable(),
    routeLocalities:
      yup
        .array()
        .nullable(),
    startTimeSlot:
      yup
        .object()
        .required(t("message.required", { title: t("table.startTimeSlotOriginalUser") }))
        .nullable(),
    endTimeSlot:
      yup
        .object()
        .required(t("message.required", { title: t("table.endTimeSlotOriginalUser") }))
        .nullable(),
    userType: fixedUserType
      ? yup.object().nullable()
      : yup
        .object()
        .required(t("message.required", { title: t("table.user_type.name") }))
        .nullable(),
    priority_date:
      yup.array().min(2, t("message.required", { title: t("table.priority_date") })),
    originalUser:
      yup
        .object()
        .required(t("message.required", { title: tLabel("table.originalUser") }))
        .nullable()
        .test("duplicateOriginalUser", tLabel("message.duplicateUser"), (value, e) => {
          return value?.value !== e?.parent?.replacementUser?.value;
        }),
    replacementUser:
      yup
        .object()
        .required(t("message.required", { title: tLabel("table.replacementUser") }))
        .nullable()
        .test("duplicateReplacementUser", tLabel("message.duplicateUser"), (value, e) => {
          return value?.value !== e?.parent?.originalUser?.value;
        }),
    note: yup.string().nullable(),
  };
  const schema = yup.object().shape(rules);
  return schema;
};

const DetailPriorityScheduleModal = ({
  isOpenModal,
  toggleModal,
  detailData,
  fetchPrioritySchedule,
  modeDetail,
  fixedUserType = null,
}) => {
  const { t } = useTranslation("prioritySchedule");
  const tUserTypeLabel = key =>
    fixedUserType ? t(`${fixedUserType}.${key}`, { defaultValue: t(key) }) : t(key);

  const mainForm = useForm({
    resolver: yupResolver(validateSchema(t, fixedUserType)),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      hub: null,
      route: [],
      routeLocalities: [],
      startTimeSlot: null,
      endTimeSlot: null,
      userType: null,
      priority_date: [],
      originalUser: null,
      replacementUser: null,
      note: null,
    },
  });

  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    reset,
    watch,
    handleSubmit,
    setValue,
  } = mainForm;

  const { hub, userType, originalUser, route, routeLocalities } = watch();
  const firstRoute = route?.[0] || null;

  const isRunner = userType?.value === userTypeMode.RUNNER;

  const onSuccessMutate = () => {
    fetchPrioritySchedule();
    onCloseModal();
  };

  const { mutate: mutateCreatePrioritySchedule, isLoading: isLoadingCreatePrioritySchedule } = useCreatePrioritySchedule({
    onSuccess: onSuccessMutate,
    onError: (error) => {
    },
  });

  const { mutate: mutateUpdatePrioritySchedule, isLoading: isLoadingUpdatePrioritySchedule } = useUpdatePrioritySchedule({
    onSuccess: onSuccessMutate,
    onError: (error) => {
    },
  });

  const onSubmit = (data) => {
    const { hub, userType, route, routeLocalities, startTimeSlot, endTimeSlot, originalUser, replacementUser, note, priority_date } = data;
    if (detailData) {
      const dataUpdate = {
        id: detailData.id,
        data: {
          original_user_id: originalUser?.value,
          replacement_user_id: replacementUser?.value,
          start_timeslot_id: startTimeSlot?.value,
          end_timeslot_id: endTimeSlot?.value,
          note: note,
          routes: route?.map(item => ({
            route_id: item?.value,
            route_locality_ids: routeLocalities?.map(item => item?.value) || null,
          })),
        },
      };
      mutateUpdatePrioritySchedule({
        id: dataUpdate.id,
        data: dataUpdate.data,
      });
    } else {
      const formatPriorityDate = priority_date.map(item => moment(item).format("YYYY-MM-DD"));
      const dataCreate = {
        hub_id: hub?.value,
        user_type: userType?.value,
        original_user_id: originalUser?.value,
        replacement_user_id: replacementUser?.value,

        start_timeslot_id: startTimeSlot?.value,
        end_timeslot_id: endTimeSlot?.value,
        priority_start_date: formatPriorityDate[0],
        priority_end_date: formatPriorityDate[1],
        note: note,
        routes: route?.map(item => ({
          route_id: item?.value,
          route_locality_ids: routeLocalities?.map(item => item?.value) || null,
        })),
      };
      mutateCreatePrioritySchedule({ data: dataCreate });
    };
  }

  const onCloseModal = () => {
    reset({});
    toggleModal(false);
  };

  useEffect(() => {
    if (isOpenModal) {
      if (!detailData) {
        reset({
          priority_date: [new Date()],
          userType: fixedUserType ? getUserTypeOption(t, fixedUserType) : null,
        });
        return;
      }
      const { hub, routes, start_timeslot, end_timeslot, user_type, priority_start_date, priority_end_date, original_user, replacement_user, note } = detailData;
      const userTypeSelect = getUserTypeOption(t, user_type);
      const routeLocalities = routes?.[0]?.route_localities || [];
      reset({
        hub: {
          value: hub?.id,
          label: hub?.name,
          data: {
            ...hub,
          }
        },
        route: routes?.map(item => ({
          value: item?.route_id,
          label: item?.name,
          data: {
            id: item?.route_id,
            ...item,
          }
        })),
        routeLocalities: routeLocalities?.map(item => ({
          value: item?.route_locality_id,
          label: item?.name,
          data: {
            id: item?.route_locality_id,
            ...item,
          }
        })),
        startTimeSlot: {
          value: start_timeslot?.id,
          label: start_timeslot?.name,
          data: {
            ...start_timeslot,
          }
        },
        endTimeSlot: {
          value: end_timeslot?.id,
          label: end_timeslot?.name,
          data: {
            ...end_timeslot,
          }
        },
        userType: userTypeSelect,
        priority_date: [
          moment(priority_start_date).toDate(),
          moment(priority_end_date).toDate(),
        ],
        originalUser: {
          value: original_user?.id,
          data: {
            ...original_user,
            email: original_user?.username,
            display_name: original_user?.display_name,
          }
        },
        replacementUser: {
          value: replacement_user?.id,
          data: {
            ...replacement_user,
            email: replacement_user?.username,
            display_name: replacement_user?.display_name,
          }
        },
        note: note,
      });
    }
  }, [detailData, isOpenModal]);

  const isDisabled = modeDetail === MODE_DETAIL.VIEW ? true : false;
  const isLoadingMutate = isLoadingUpdatePrioritySchedule || isLoadingCreatePrioritySchedule;

  const clearDataDependOnHubAndUserType = () => {
    setValue("originalUser", null);
    setValue("replacementUser", null);
    setValue("route", []);
    setValue("routeLocalities", []);
    setValue("startTimeSlot", null);
    setValue("endTimeSlot", null);
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpenModal}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={onCloseModal}
    >
      <ModalHeader toggle={onCloseModal}>
        {modeDetail === MODE_DETAIL.VIEW
          ? t("title.detail")
          : modeDetail === MODE_DETAIL.EDIT
            ? t("title.update")
            : t("title.create")}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={6} md={6} className="mt-1" style={{ width: "100%" }}>
            <FormSelectModel
              isClearable={false}
              isRequired
              title={t("table.hub")}
              keyQuery={"hub"}
              modelName={"myHubs"}
              mainForm={mainForm}
              fieldName="hub"
              t={t}
              onChange={() => {
                clearDataDependOnHubAndUserType();
              }}
              isDisabled={modeDetail === MODE_DETAIL.CREATE ? false : true}
            />
          </Col>
          <Col xs={6} md={6} className="mt-1">
            <FormSelectModel
              isClearable={false}
              isRequired
              title={tUserTypeLabel("table.originalUser")}
              key={`originalUser-${userType?.value}-${hub?.value}`}
              keyQuery={`originalUser`}
              modelName={isRunner ? "runnerHub" : "collectorHub"}
              mainForm={mainForm}
              fieldName="originalUser"
              t={t}
              customColumnFilter={{
                hubId: hub?.value,
              }}
              onChange={() => {
                setValue("startTimeSlot", null);
                setValue("endTimeSlot", null);
              }}
              isDisabled={isDisabled || !hub || !userType}
            />
          </Col>
          <Col xs={6} md={6} className="mt-1">
            <FormSelectModel
              isClearable={false}
              isRequired
              title={tUserTypeLabel("table.replacementUser")}
              key={`replacementUser-${userType?.value}-${hub?.value}`}
              keyQuery={"replacementUser"}
              modelName={isRunner ? "runnerHub" : "collectorHub"}
              mainForm={mainForm}
              fieldName="replacementUser"
              t={t}
              customColumnFilter={{
                hubId: hub?.value,
              }}
              isDisabled={isDisabled || !hub || !userType}
            />
          </Col>
          <Col xs={12} md={12} className="mt-1">
            <FormSelectModel
              isMultiple
              isClearable={true}
              isRequired
              title={`${t("table.route")} (${route?.length || 0})`}
              key={`route-${userType?.value}-${hub?.value}`}
              modelName={isRunner ? "routeRunner" : "routeCollector"}
              mainForm={mainForm}
              fieldName="route"
              t={t}
              customColumnFilter={{
                "filter[hub_id]": hub?.value,
              }}
              isDisabled={isDisabled || !hub || !userType}
              onChange={(valueChange) => {
                setValue("routeLocalities", []);
              }}
            />
          </Col>
          <Col xs={12} md={12} className="mt-1">
            <FormSelectModel
              isMultiple
              isClearable={true}
              title={`${t("table.routeLocalities")} ${route?.length === 1 ? `- ${firstRoute?.data?.code} (${routeLocalities?.length})` : ''}`}
              key={`routeLocalities-${firstRoute?.value}`}
              modelName={isRunner ? "routeLocalitiesRunner" : "routeLocalitiesCollector"}
              mainForm={mainForm}
              fieldName="routeLocalities"
              t={t}
              customColumnFilter={{
                "filter[route_id]": firstRoute?.value,
              }}
              isDisabled={isDisabled || !userType || route?.length !== 1}
            />
          </Col>
          <Col xs={6} md={6} className="mt-1">
            <FormSelectModel
              isClearable={false}
              isRequired
              title={`${t("table.startTimeSlotOriginalUser")}`}
              key={`startTimeSlot-${userType?.value}-${hub?.value}`}
              modelName={isRunner ? "timeSlotRunner" : "timeSlotCollector"}
              mainForm={mainForm}
              fieldName="startTimeSlot"
              t={t}
              customColumnFilter={{
                "filter[hub_id]": hub?.value,
                // "user_code": originalUser?.data?.code,
                "filter[type][in]": isRunner ? [0] : undefined,
              }}
              isDisabled={isDisabled || !hub || !userType}
            />
          </Col>
          <Col xs={6} md={6} className="mt-1">
            <FormSelectModel
              isClearable={false}
              isRequired
              title={`${t("table.endTimeSlotOriginalUser")}`}
              key={`endTimeSlot-${userType?.value}-${hub?.value}`}
              modelName={isRunner ? "timeSlotRunner" : "timeSlotCollector"}
              mainForm={mainForm}
              fieldName="endTimeSlot"
              t={t}
              customColumnFilter={{
                "filter[hub_id]": hub?.value,
                // "user_code": originalUser?.data?.code,
                "filter[type][in]": isRunner ? [0] : undefined,
              }}
              isDisabled={isDisabled || !hub || !userType}
            />
          </Col>
          <Col xs={12} md={12} className="mt-1">
            <FormDateV2
              isNotPastDate={true}
              isRequired
              title={t("table.priority_date")}
              mainForm={mainForm}
              fieldName="priority_date"
              isDateRange={true}
              t={t}
              isDisabled={modeDetail !== MODE_DETAIL.CREATE}
            />
          </Col>

          <Col xs={12} md={12} className="mt-1">
            <FormInput
              mainForm={mainForm}
              fieldName="note"
              t={t}
              type="textarea"
              title={t("table.note")}
              placeholder={t("common:placeholder.enter", { title: t("table.note") })}
              rows={3}
              isDisabled={isDisabled}
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          size="xs"
          color="danger"
          onClick={onCloseModal}
          className="mx-3"
        >
          {t("button.cancel")}
        </Button>
        {[MODE_DETAIL.CREATE, MODE_DETAIL.EDIT].includes(modeDetail) ? (
          <Button
            size="xs"
            color="primary"
            style={{ backgroundColor: "#3C64B1" }}
            disabled={isLoadingMutate}
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
          >
            {t("button.submit")}
          </Button>
        ) : <></>}

      </ModalFooter>
    </Modal>
  );
};

export default DetailPriorityScheduleModal;
